import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { DownloadIcon, SearchIcon, ChevronDownIcon, XIcon, ClockIcon, CalendarIcon, ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon } from 'lucide-react';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { nowMs } from '../../../shared/lib/relativeDates';
import { getBalanceEntries, getGroupingFieldForBucket, type ActivityType, type BalanceTypeKey } from '../mocks/balanceData';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
import { DateRangePicker } from '../../analytics/components/DateRangePicker';
type BalanceType = 'Available' | 'Incoming' | 'Reserved' | 'On hold';
const ACTIVITY_TYPES: ActivityType[] = ['Payment', 'Settlement', 'Reserve hold', 'Reserve release', 'Refund', 'Void', 'Chargeback', 'Chargeback fee', 'Chargeback reversal', 'Processing fee', 'Payout', 'Review hold', 'Review release', 'Adjustment'];
interface ActivityEntry {
  id: string;
  effectiveDate: string;
  date: Date;
  balanceType: BalanceType;
  activityType: ActivityType;
  reference: string;
  orderRef: string | null;
  amountChange: number;
  companyId: string;
  availableOn: Date | null;
  payoutDate: Date | null;
}
const balanceTypeColors: Record<BalanceType, string> = {
  Available: 'bg-green-500',
  Incoming: 'bg-amber-400',
  Reserved: 'bg-indigo-500',
  'On hold': 'bg-red-400'
};
const allActivity: ActivityEntry[] = (() => {
  const entries = getBalanceEntries();
  return entries.map(e => ({
    id: e.id,
    effectiveDate: e.effectiveDate,
    date: e.date,
    balanceType: e.balanceType as BalanceType,
    activityType: e.activityType as ActivityType,
    reference: e.reference,
    orderRef: e.orderRef,
    amountChange: e.amount,
    companyId: e.companyId,
    availableOn: e.availableOn,
    payoutDate: e.payoutDate
  })).sort((a, b) => b.date.getTime() - a.date.getTime());
})();
const PAGE_SIZE = 10;
const BALANCE_TYPE_PARAM_MAP: Record<string, string> = {
  incoming: 'Incoming',
  available: 'Available',
  reserved: 'Reserved',
  onHold: 'On hold'
};
const formatShortDate = (d: Date) => d.toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'short'
});
// ── Date range filtering ──────────────────────────────────────────────
// Per-column filters use the shared DateRangePicker. To represent the
// "All time" (no filter) state without making the picker's value optional,
// we use a wide sentinel range and detect it with isAllTime().
type DateRange = {
  start: Date;
  end: Date;
};
const ALL_TIME_START = new Date(2020, 0, 1);
const ALL_TIME_END = new Date(2030, 11, 31);
const allTimeRange = (): DateRange => ({
  start: new Date(ALL_TIME_START),
  end: new Date(ALL_TIME_END)
});
const isAllTime = (r: DateRange) => r.start.getTime() <= ALL_TIME_START.getTime() + 1000 && r.end.getTime() >= ALL_TIME_END.getTime() - 1000;
function dateRangePredicate(range: DateRange, date: Date | null): boolean {
  if (isAllTime(range)) return true;
  if (!date) return false;
  const startMs = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate(), 0, 0, 0, 0).getTime();
  const endMs = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate(), 23, 59, 59, 999).getTime();
  const ms = date.getTime();
  return ms >= startMs && ms <= endMs;
}
const formatDateTime = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${mins}`;
};
/**
 * "Available on" answers: when does/did this entry's funds sit in Available?
 * - Available bucket entries: the moment they hit Available = effectiveDate
 * - Incoming Payment / Reserved Reserve hold: their availableOn (settlement / release date)
 * - Incoming Settlement (debit) / Reserved Reserve release (debit): same day they
 *   move into Available = their effectiveDate
 * - On hold: funds aren't available — show "Under review"
 */
function resolveAvailableOn(entry: ActivityEntry): Date | null {
  if (entry.balanceType === 'On hold') return null;
  if (entry.availableOn) return entry.availableOn;
  // For all remaining entries the funds touch Available on the entry date itself
  return entry.date;
}
function getSortValue(entry: ActivityEntry, key: SortKey): {
  num: number | null;
  str: string | null;
} {
  switch (key) {
    case 'effective':
      return {
        num: entry.date.getTime(),
        str: null
      };
    case 'availableOn':
      {
        const d = resolveAvailableOn(entry);
        return {
          num: d ? d.getTime() : null,
          str: null
        };
      }
    case 'payoutDate':
      return {
        num: entry.payoutDate ? entry.payoutDate.getTime() : null,
        str: null
      };
    case 'balanceType':
      return {
        num: null,
        str: entry.balanceType
      };
    case 'activityType':
      return {
        num: null,
        str: entry.activityType
      };
    case 'id':
      return {
        num: null,
        str: entry.id
      };
    case 'amount':
      return {
        num: entry.amountChange,
        str: null
      };
  }
}
function compareEntries(a: ActivityEntry, b: ActivityEntry, sort: SortState): number {
  const av = getSortValue(a, sort.key);
  const bv = getSortValue(b, sort.key);
  // Nulls always last regardless of direction
  if (av.num === null && bv.num === null && av.str === null && bv.str === null) return 0;
  if (av.num === null && av.str === null) return 1;
  if (bv.num === null && bv.str === null) return -1;
  let cmp = 0;
  if (av.num !== null && bv.num !== null) {
    cmp = av.num - bv.num;
  } else if (av.str !== null && bv.str !== null) {
    cmp = av.str.localeCompare(bv.str);
  }
  if (cmp === 0) {
    // Tie-breaker: newest first by effective date
    cmp = a.date.getTime() - b.date.getTime();
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}
type SortKey = 'effective' | 'availableOn' | 'payoutDate' | 'balanceType' | 'activityType' | 'id' | 'amount';
type SortDir = 'asc' | 'desc';
interface SortState {
  key: SortKey;
  dir: SortDir;
}
const DEFAULT_SORT: SortState = {
  key: 'effective',
  dir: 'desc'
};
function HeaderCell({
  label,
  tooltip,
  align = 'left',
  sortKey,
  currentSort,
  onSort
}: {
  label: string;
  tooltip: string;
  align?: 'left' | 'right';
  sortKey?: SortKey;
  currentSort?: SortState;
  onSort?: (key: SortKey) => void;
}) {
  const isActive = !!sortKey && currentSort?.key === sortKey;
  const SortIcon = isActive ? currentSort?.dir === 'asc' ? ArrowUpIcon : ArrowDownIcon : ArrowUpDownIcon;
  const ariaSort = isActive ? currentSort?.dir === 'asc' ? 'ascending' : 'descending' : 'none';
  return <th scope="col" aria-sort={sortKey ? ariaSort as any : undefined} className={`px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-2799">
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`} data-id="element-2800">
        {sortKey && onSort ? <button type="button" onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}${isActive ? `, currently ${currentSort?.dir === 'asc' ? 'ascending' : 'descending'}` : ''}`} className={`inline-flex items-center gap-1 rounded px-0.5 -mx-0.5 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 transition-colors ${isActive ? 'text-gray-900' : ''}`} data-id="element-2801">
            <span data-id="element-2802">{label}</span>
            <SortIcon size={12} className={isActive ? 'text-gray-900' : 'text-gray-400'} aria-hidden="true" data-id="element-2803" />
          </button> : label}
        <InfoTooltip content={tooltip} label={`What is ${label}?`} placement="bottom" align={align === 'right' ? 'right' : 'left'} data-id="element-2804" />
      </span>
    </th>;
}
function DateRangeFilter({
  label,
  value,
  onChange,
  disabled
}: {
  label: string;
  value: DateRange;
  onChange: (v: DateRange) => void;
  disabled?: boolean;
}) {
  const cleared = isAllTime(value);
  // When no filter is set we render a single picker-styled button reading
  // "All time" so there's no confusing date range visible alongside an
  // "All time" hint. Clicking it seeds the picker with a sensible default
  // (last 7 days) so the user can immediately tweak from there.
  const handleActivate = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    onChange({
      start,
      end
    });
  };
  return <div className={`inline-flex items-center gap-1 ${disabled ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-2805">
      <span className="text-xs font-medium text-gray-600" data-id="element-2806">{label}:</span>
      {cleared ? <button type="button" onClick={handleActivate} aria-label={`Set ${label} date range`} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500" data-id="element-2807">
          <CalendarIcon size={16} aria-hidden="true" data-id="element-2808" />
          <span data-id="element-2809">All time</span>
        </button> : <>
          <DateRangePicker value={value} onChange={onChange} data-id="element-2810" />
          <button type="button" onClick={() => onChange(allTimeRange())} aria-label={`Clear ${label} filter`} className="p-1.5 text-gray-500 hover:text-gray-900 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 transition-colors" data-id="element-2811">
            <XIcon size={12} aria-hidden="true" data-id="element-2812" />
          </button>
        </>}
    </div>;
}
export function BalanceActivity() {
  const {
    t
  } = useTranslation('balance');
  const {
    selectedCompany
  } = useCompany();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || '';
  const dateParam = searchParams.get('date') || '';
  const [searchQuery, setSearchQuery] = useState('');
  const [balanceFilter, setBalanceFilter] = useState(BALANCE_TYPE_PARAM_MAP[typeParam] || 'All balances');
  const [activityFilter, setActivityFilter] = useState('All activity');
  const [effectiveDateFilter, setEffectiveDateFilter] = useState<DateRange>(allTimeRange());
  const [availableOnFilter, setAvailableOnFilter] = useState<DateRange>(allTimeRange());
  const [payoutDateFilter, setPayoutDateFilter] = useState<DateRange>(allTimeRange());
  const [dateChip, setDateChip] = useState(dateParam);
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const handleSort = (key: SortKey) => {
    setSort(cur => {
      if (cur.key !== key) {
        // First click on a new column: sort descending for date/amount
        // (most users want newest/biggest first), ascending for text.
        const isNumeric = key === 'effective' || key === 'availableOn' || key === 'payoutDate' || key === 'amount';
        return {
          key,
          dir: isNumeric ? 'desc' : 'asc'
        };
      }
      return {
        key,
        dir: cur.dir === 'asc' ? 'desc' : 'asc'
      };
    });
    setCurrentPage(1);
  };
  useEffect(() => {
    if (typeParam && BALANCE_TYPE_PARAM_MAP[typeParam]) {
      setBalanceFilter(BALANCE_TYPE_PARAM_MAP[typeParam]);
    }
    setDateChip(dateParam);
    setCurrentPage(1);
  }, [typeParam, dateParam]);
  const clearDateChip = () => {
    setDateChip('');
    const next = new URLSearchParams(searchParams);
    next.delete('date');
    setSearchParams(next, {
      replace: true
    });
  };
  const formatCurrency = (amount: number) => formatCurrencyAmount(Math.abs(amount), {
    fractionDigits: 2
  });
  const filteredActivity = useMemo(() => {
    return allActivity.filter(entry => {
      const matchesCompany = selectedCompany.type === 'master' || entry.companyId === selectedCompany.id;
      const q = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === '' || entry.id.toLowerCase().includes(q) || entry.reference.toLowerCase().includes(q) || entry.activityType.toLowerCase().includes(q);
      const matchesBalance = balanceFilter === 'All balances' || entry.balanceType === balanceFilter;
      const matchesActivity = activityFilter === 'All activity' || entry.activityType === activityFilter;
      const availableOn = resolveAvailableOn(entry);
      const matchesEffective = dateChip ? true : dateRangePredicate(effectiveDateFilter, entry.date);
      const matchesAvailableOn = dateRangePredicate(availableOnFilter, availableOn);
      const matchesPayout = dateRangePredicate(payoutDateFilter, entry.payoutDate);
      let matchesDateChip = true;
      if (dateChip) {
        const bucketKey = ({
          Incoming: 'incoming',
          Available: 'available',
          Reserved: 'reserved',
          'On hold': 'onHold'
        } as Record<BalanceType, BalanceTypeKey>)[entry.balanceType];
        const field = getGroupingFieldForBucket(bucketKey);
        const target = field === 'date' ? entry.date : field === 'availableOn' ? entry.availableOn : entry.payoutDate;
        matchesDateChip = !!target && formatShortDate(target) === dateChip;
      }
      return matchesCompany && matchesSearch && matchesBalance && matchesActivity && matchesEffective && matchesAvailableOn && matchesPayout && matchesDateChip;
    });
  }, [selectedCompany, searchQuery, balanceFilter, activityFilter, effectiveDateFilter, availableOnFilter, payoutDateFilter, dateChip]);
  const sortedActivity = useMemo(() => {
    return filteredActivity.slice().sort((a, b) => compareEntries(a, b, sort));
  }, [filteredActivity, sort]);
  const totalEntries = sortedActivity.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalEntries);
  const currentEntries = sortedActivity.slice(startIndex, endIndex);
  const now = nowMs();
  // The "Now" divider only makes sense when entries are in their natural
  // chronological order (default sort). Any other sort breaks the
  // before/after ordering, so hide it.
  const isDefaultSort = sort.key === DEFAULT_SORT.key && sort.dir === DEFAULT_SORT.dir;
  const hasFutureInPage = currentEntries.some(e => e.date.getTime() > now);
  const hasPastInPage = currentEntries.some(e => e.date.getTime() <= now);
  const showNowDivider = isDefaultSort && hasFutureInPage && hasPastInPage;
  const firstPastIndex = showNowDivider ? currentEntries.findIndex(e => e.date.getTime() <= now) : -1;
  const COL_COUNT = 7;
  return <div className="max-w-7xl mx-auto" data-id="element-2813">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-2814">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-2815">
          {t('pages.BalanceActivity.balance_activity')}
        </h1>
        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-2816">
          <DownloadIcon size={16} data-id="element-2817" />
          {t('pages.BalanceActivity.export')}
        </button>
      </div>

      {/* Active filter chip from deep link */}
      {dateChip && (() => {
      const bucketKey = BALANCE_TYPE_PARAM_MAP[typeParam] !== undefined ? typeParam as BalanceTypeKey : null;
      const field = bucketKey ? getGroupingFieldForBucket(bucketKey as BalanceTypeKey) : 'date';
      const fieldLabel = field === 'availableOn' ? 'Available on' : field === 'payoutDate' ? 'Payout date' : 'Effective date';
      return <div className="mb-4 flex items-center gap-2" data-id="element-2818">
              <span className="text-xs text-gray-600" data-id="element-2819">Filtered by:</span>
              <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-full" data-id="element-2820">
                {fieldLabel}: {dateChip} {new Date().getFullYear()}
                <button type="button" onClick={clearDateChip} aria-label={`Clear ${fieldLabel} filter`} className="p-1.5 -my-0.5 text-gray-500 hover:text-gray-900 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 transition-colors" data-id="element-2821">
                  <XIcon size={12} aria-hidden="true" data-id="element-2822" />
                </button>
              </span>
            </div>;
    })()}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2823">
        <div className="flex items-center gap-3 p-4 border-b border-gray-200 flex-wrap" data-id="element-2824">
          <div className="relative flex-1 min-w-[220px]" data-id="element-2825">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2826" />
            <input type="text" placeholder="Search by balance txn ID or activity" value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-400 transition-colors" data-id="element-2827" />
          </div>

          <div className="relative" data-id="element-2828">
            <select value={balanceFilter} onChange={e => {
            setBalanceFilter(e.target.value);
            setCurrentPage(1);
          }} aria-label="Filter by balance type" className="appearance-none pl-3 pr-8 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1 focus:border-yellow-500 transition-colors" data-id="element-2829">
              <option data-id="element-2830">All balances</option>
              <option data-id="element-2831">Available</option>
              <option data-id="element-2832">Incoming</option>
              <option data-id="element-2833">Reserved</option>
              <option data-id="element-2834">On hold</option>
            </select>
            <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" data-id="element-2835" />
          </div>

          <div className="relative" data-id="element-2836">
            <select value={activityFilter} onChange={e => {
            setActivityFilter(e.target.value);
            setCurrentPage(1);
          }} aria-label="Filter by activity type" className="appearance-none pl-3 pr-8 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1 focus:border-yellow-500 transition-colors" data-id="element-2837">
              <option data-id="element-2838">All activity</option>
              {ACTIVITY_TYPES.map(opt => <option key={opt} data-id="element-2839">{opt}</option>)}
            </select>
            <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-2840" />
          </div>
        </div>

        {/* Per-column date filters */}
        <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-200 flex-wrap bg-gray-50/40" data-id="element-2841">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2842">
            Date filters
          </span>
          <DateRangeFilter label="Effective" value={effectiveDateFilter} onChange={v => {
          setEffectiveDateFilter(v);
          setCurrentPage(1);
        }} disabled={!!dateChip} data-id="element-2843" />
          <DateRangeFilter label="Available on" value={availableOnFilter} onChange={v => {
          setAvailableOnFilter(v);
          setCurrentPage(1);
        }} data-id="element-2844" />
          <DateRangeFilter label="Payout" value={payoutDateFilter} onChange={v => {
          setPayoutDateFilter(v);
          setCurrentPage(1);
        }} data-id="element-2845" />
        </div>

        {/* Table */}
        <div className="overflow-x-auto overflow-y-visible" data-id="element-2846">
          <table className="w-full" data-id="element-2847">
            <thead data-id="element-2848">
              <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-2849">
                <HeaderCell label="Effective date" tooltip="When this entry was recorded in this balance bucket." sortKey="effective" currentSort={sort} onSort={handleSort} data-id="element-2850" />
                <HeaderCell label="Available on" tooltip="When the funds reach (or reached) your Available balance and become spendable. Shown for every entry that touches Available." sortKey="availableOn" currentSort={sort} onSort={handleSort} data-id="element-2851" />
                <HeaderCell label="Payout date" tooltip="The payout cycle this entry falls into — when funds leave your Available balance for your bank. Blank for entries held in On hold or voided before settlement." sortKey="payoutDate" currentSort={sort} onSort={handleSort} data-id="element-2852" />
                <HeaderCell label="Balance type" tooltip="Which balance bucket this entry affects: Incoming, Available, Reserved, or On hold." sortKey="balanceType" currentSort={sort} onSort={handleSort} data-id="element-2853" />
                <HeaderCell label="Activity type" tooltip="The economic event behind this entry — Payment, Settlement, Refund, Chargeback, Payout, etc." sortKey="activityType" currentSort={sort} onSort={handleSort} data-id="element-2854" />
                <HeaderCell label="Balance transaction ID" tooltip="The unique ID for this balance ledger entry (bt_…). Open the entry to see the linked order, payout, fee or dispute." sortKey="id" currentSort={sort} onSort={handleSort} data-id="element-2855" />
                <HeaderCell label="Amount change" tooltip="How much this entry adds to (+) or removes from (−) the balance type." align="right" sortKey="amount" currentSort={sort} onSort={handleSort} data-id="element-2856" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-2857">
              {currentEntries.length === 0 && <tr data-id="element-2858">
                  <td colSpan={COL_COUNT} className="px-5 py-12 text-center text-sm text-gray-500" data-id="element-2859">
                    No activity matches your filters.
                  </td>
                </tr>}
              {currentEntries.map((entry, index) => {
              const isFuture = entry.date.getTime() > now;
              const showDividerBefore = index === firstPastIndex;
              const availableOn = resolveAvailableOn(entry);
              const futureMutedClass = isFuture ? 'text-gray-500' : 'text-gray-700';
              return <Fragment key={`${entry.reference}-${index}`} data-id="element-2860">
                    {showDividerBefore && <tr className="bg-gray-50/60" aria-hidden="true" data-id="element-2861">
                        <td colSpan={COL_COUNT} className="px-5 py-2 text-[11px] font-semibold text-gray-600 uppercase tracking-wider" data-id="element-2862">
                          <span aria-hidden="true" data-id="element-2863">──</span> Now{' '}
                          <span aria-hidden="true" data-id="element-2864">──</span>
                        </td>
                      </tr>}
                    <tr tabIndex={0} role="link" aria-label={`${isFuture ? 'Scheduled — ' : ''}${entry.activityType}, ${entry.balanceType}, ${entry.amountChange >= 0 ? '+' : '-'}${formatCurrency(entry.amountChange)} on ${entry.effectiveDate}. View details.`} onClick={() => {
                  const href = entry.activityType === 'Payout' ? `/payouts/${entry.reference}` : `/balance-activity/${entry.id}`;
                  navigate(href);
                }} onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const href = entry.activityType === 'Payout' ? `/payouts/${entry.reference}` : `/balance-activity/${entry.id}`;
                    navigate(href);
                  }
                }} className={`hover:bg-gray-50/50 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-inset ${isFuture ? 'text-gray-600' : ''}`} data-id="element-2865">
                      <td className="px-5 py-4 text-sm whitespace-nowrap" data-id="element-2866">
                        <div className="flex items-center gap-2" data-id="element-2867">
                          {isFuture && <>
                              <ClockIcon size={13} className="text-gray-500" aria-hidden="true" data-id="element-2868" />
                              <span className="sr-only" data-id="element-2869">Scheduled — </span>
                            </>}
                          <span className={futureMutedClass} data-id="element-2870">
                            {entry.effectiveDate}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm whitespace-nowrap" data-id="element-2871">
                        {availableOn ? <span className={futureMutedClass} data-id="element-2872">
                            {formatDateTime(availableOn)}
                          </span> : <span className="text-gray-600" data-id="element-2873">Under review</span>}
                      </td>
                      <td className="px-5 py-4 text-sm whitespace-nowrap" data-id="element-2874">
                        {entry.payoutDate ? <span className={futureMutedClass} data-id="element-2875">
                            {formatDateTime(entry.payoutDate)}
                          </span> : <span className="text-gray-400" aria-hidden="true" data-id="element-2876">
                            —
                          </span>}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" data-id="element-2877">
                        <div className="flex items-center gap-2" data-id="element-2878">
                          <div className={`w-2.5 h-2.5 rounded-full ${balanceTypeColors[entry.balanceType]}`} aria-hidden="true" data-id="element-2879"></div>
                          <span className={`text-sm ${futureMutedClass}`} data-id="element-2880">
                            {entry.balanceType}
                          </span>
                        </div>
                      </td>
                      <td className={`px-5 py-4 text-sm whitespace-nowrap ${futureMutedClass}`} data-id="element-2881">
                        {entry.activityType}
                      </td>
                      <td className="px-5 py-4 text-sm whitespace-nowrap" data-id="element-2882">
                        <span className={`font-mono ${futureMutedClass}`} title="Balance transaction ID" data-id="element-2883">
                          {entry.id}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-right whitespace-nowrap" data-id="element-2884">
                        <span className={entry.amountChange >= 0 ? isFuture ? 'text-emerald-700/80' : 'text-emerald-700' : 'text-gray-900'} data-id="element-2885">
                          {entry.amountChange >= 0 ? '+' : '-'}
                          {formatCurrency(entry.amountChange)}
                        </span>
                      </td>
                    </tr>
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200" data-id="element-2886">
          <span className="text-sm text-gray-600" aria-live="polite" aria-atomic="true" data-id="element-2887">
            Showing {totalEntries === 0 ? 0 : startIndex + 1} to {endIndex} of{' '}
            {totalEntries} entries
          </span>
          <div className="flex items-center gap-2" data-id="element-2888">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-2889">
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-2890">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}